import { useRef } from 'react';

import { createPortal } from 'react-dom';
import { FormProvider, SubmitHandler } from 'react-hook-form';

import CloseIcon from '@/assets/icons/icon-close-small.svg';
import NumberWithTooltip from '@/components/shared/tooltips/NumberTooltip';
import { WalletInfoAlternate } from '@/components/widgets/WalletInfo';
import useLoanForm from '@/hooks/loans/useLoanForm';
import { useBestTrait } from '@/hooks/query/loans/useBestTrait';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { displayRepay } from '@/utils/formatter';
import { cn } from '@/utils/helper';
import { IDesiredTerms, ILoanFormModalFields } from 'types/loan';

import ChangableDesiredTerms from './ChangableDesiredTerms';
import PotentialProfit from './PotentialProfit';

export interface ILoanFormModalProps extends ILoanFormModalFields {
  onSubmit: SubmitHandler<
    IDesiredTerms & {
      termUnix?: number;
    }
  >;
  handleClose: () => void;
  collectionAddress: string;
  name: string;
}

export default function LoanFormModal({ isCollectionModal, termsTitle, termsSubtitle, collectionName, collectionAddress, floorPrice, tokenId, name, imageUrl, onSubmit, handleClose }: ILoanFormModalProps) {
  const methods = useLoanForm();
  const modalRef = useRef(null);
  useOnClickOutside(modalRef, () => {
    handleClose();
  });
  const { data: bestTrait, isLoading: bestTraitLoading } = useBestTrait(collectionAddress, tokenId, {
    enabled: !!collectionAddress && !!tokenId && !isCollectionModal,
  });

  return createPortal(
    <FormProvider {...methods}>
      <div className='absolute bg-white dark:bg-jet-black w-screen h-screen top-0 z-30 flex justify-center items-center'>
        <div ref={modalRef} className='bg-white-100  dark:bg-jet-black-100 border max-w-[875px]'>
          <div className={cn('p-6 flex justify-between gap-11 items-center ', isCollectionModal ? '' : 'border-b')}>
            <p className='truncate text-xl font-semibold !text-coal-250 dark:!text-white max-w-[calc(100%-256px)]'>
              {isCollectionModal ? 'Make a Collection offer ' : 'Create a Loan Request'} for {collectionName} #{tokenId}
            </p>
            <div className='gap-4 flex'>
              <WalletInfoAlternate className='h-8 !bg-white [&_.text]:!text-primary' />
              <button onClick={handleClose}>
                <CloseIcon className='[&_.line]:fill-primary dark:[&_.line]:fill-primary-blue-100' />
              </button>
            </div>
          </div>
          <div>
            <div className='flex border-b'>
              <div className={cn('py-[25px] px-8 flex flex-col justify-center items-center w-max', isCollectionModal ? 'border-t' : 'border-r')}>
                <span className='text-base font-semibold truncate max-w-[192px]'>{collectionName}</span>
                {!isCollectionModal && <span className='text-xs font-normal truncate max-w-[192px]'>{name || `#${tokenId}`}</span>}
                <img className='size-[190px] rounded-none mt-0.5' alt='NFT image' src={imageUrl || '/bayc-collectors.png'} />
                <div className={cn('flex w-full', isCollectionModal ? 'justify-center text-center' : 'justify-between')}>
                  <NumberWithTooltip fullNumber={floorPrice || 'N/A'} symbol='ETH'>
                    <div className='flex flex-col mt-1'>
                      <span className='font-medium text-xs'>Floor</span>
                      <span className='text-xs font-space-mono font-normal'>{floorPrice ? displayRepay(floorPrice) : 'N/A'} ETH</span>
                    </div>
                  </NumberWithTooltip>
                  {!isCollectionModal &&
                    (bestTraitLoading ? (
                      <div className='loader !h-[30px] mx-auto' />
                    ) : (
                      <div className='flex flex-col mt-1'>
                        <span className='font-medium text-xs text-right'>Trait</span>
                        <span className='text-xs font-space-mono font-normal'>{bestTrait ? `${bestTrait.maxTraitPrice} ETH` : 'N/A'}</span>
                      </div>
                    ))}
                </div>
              </div>
              <div className='p-8 flex justify-center flex-grow'>
                <div className='flex justify-center flex-col gap-4'>
                  <ChangableDesiredTerms title={termsTitle ?? { simple: '' }} subtitle={termsSubtitle ?? ''} className='max-h-max m-0' assetValue={floorPrice || 0} />
                  {isCollectionModal && <PotentialProfit className={isCollectionModal ? 'text-center' : ''} />}
                </div>
              </div>
            </div>
          </div>
          <div className='flex justify-center py-6 gap-[14px]'>
            <button onClick={methods.handleSubmit(onSubmit)} className='bg-primary rounded py-3 px-6 text-white text-sm font-medium'>
              Create
            </button>
            <button onClick={handleClose} className='text-coal-250 bg-white dark:bg-jet-black rounded dark:text-white px-6 py-3 text-sm font-medium'>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </FormProvider>,
    document.querySelector('#app')!
  );
}
